import { useUrlParam } from '@leagueplatform/web-common';
import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  Button,
  HeadingText,
  ImageMessage,
  ParagraphText,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { useGetIsImpersonator } from '@leagueplatform/auth-utils';
import React from 'react';
import {
  MODAL_PARAMS,
  useQueryModalContent,
  VALID_CUSTOM_MODAL_PARAMS,
  VALID_MODAL_PARAMS,
  IMPERSONATION_BLOCK_MODAL_PARAMS,
} from 'hooks/use-query-modal-content.hook';
import {
  QUERY_PARAM_KEYS,
  WEBVIEW_MODAL_QUERY_PARAM_VALUE,
} from 'common/sso-constants';
import { ModalWrapper } from 'components/modal-wrapper/modal-wrapper.component';
import { ImpersonationErrorDisplayModalComponent } from 'components/impersonation-error/impersonation-error-display-modal.component';
import { useNavigate } from '@leagueplatform/routing';

/**
 *  A Pop up modal that is displayed based on URL query parameter values.
 *
 * @param additionalCustomModalParams -  custom modal params that can be passed to enable modal on this specific app. Must already be supported by this component.
 * @returns A react component implementation of a pop up modal
 */
export const QueryParamModal = ({
  additionalCustomModalParams = [],
}: {
  additionalCustomModalParams?: string[];
}) => {
  const isImpersonation = useGetIsImpersonator();
  const { formatMessage } = useIntl();
  const { removeParam, urlParam } = useUrlParam(QUERY_PARAM_KEYS.modal);
  const navigate = useNavigate();

  const { customComponent, ...rest } = useQueryModalContent(
    urlParam as MODAL_PARAMS,
  );

  const handleClose = () => {
    navigate(-1);
  };

  if (
    isImpersonation &&
    urlParam !== null &&
    IMPERSONATION_BLOCK_MODAL_PARAMS.includes(urlParam as MODAL_PARAMS)
  ) {
    return <ImpersonationErrorDisplayModalComponent isOpen />;
  }

  if (
    [...VALID_CUSTOM_MODAL_PARAMS, ...additionalCustomModalParams].includes(
      urlParam as MODAL_PARAMS,
    ) &&
    Boolean(customComponent)
  ) {
    const Modal = customComponent as React.ElementType;
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <Modal {...rest} onClose={handleClose} />;
  }

  const {
    label,
    heading,
    body,
    bodyTwo,
    cancelButton,
    submitButton,
    styling,
    image,
  } = rest;

  if (
    !VALID_MODAL_PARAMS.includes(urlParam as MODAL_PARAMS) ||
    !label ||
    !heading ||
    !body
  ) {
    // remove the modal param unless it is for the authed webview
    if (urlParam !== WEBVIEW_MODAL_QUERY_PARAM_VALUE) {
      removeParam();
    }
    return null;
  }

  return (
    <ModalWrapper
      onClose={handleClose}
      modalLabel={formatMessage({ id: label })}
      maxWidth="890px"
    >
      <StackLayout
        spacing="$one"
        css={{
          padding: '$one $three',
          '@mobile': { padding: '0' },
          '@mobileLandscape': { padding: '$one' },
          ...styling,
        }}
      >
        {image && (
          <StackLayout css={{ width: '100%' }} horizontalAlignment="center">
            <ImageMessage image={image} imageHeight={160} />
          </StackLayout>
        )}
        <Box>
          <HeadingText
            size="xl"
            level="1"
            css={{ marginBottom: '$oneAndHalf' }}
          >
            {formatMessage({ id: heading })}
          </HeadingText>
          <ParagraphText
            emphasis="subtle"
            css={{ marginBottom: '$oneAndHalf' }}
          >
            {formatMessage({ id: body })}
          </ParagraphText>
          {bodyTwo && (
            <ParagraphText emphasis="subtle">
              {formatMessage({ id: bodyTwo })}
            </ParagraphText>
          )}
        </Box>

        <StackLayout
          orientation="horizontal"
          horizontalAlignment={{ '@initial': 'end', '@mobile': 'spaceBetween' }}
          spacing="$one"
          css={{ width: '100%' }}
        >
          {cancelButton && (
            <Button
              onClick={cancelButton.onClick || handleClose}
              priority="secondary"
              quiet
            >
              {formatMessage({ id: cancelButton.id })}
            </Button>
          )}
          {submitButton && (
            <Button onClick={submitButton.onClick || handleClose}>
              {formatMessage({ id: submitButton.id })}
            </Button>
          )}
        </StackLayout>
      </StackLayout>
    </ModalWrapper>
  );
};
